import React, { useState, useRef, useEffect } from 'react';
import { FaDownload } from 'react-icons/fa';

/**
 * ChartWithDownload - A reusable wrapper component for analytics charts with CSV download functionality
 * 
 * @param {Object} props
 * @param {string} props.title - Chart title
 * @param {string} props.subtitle - Optional chart subtitle
 * @param {Array} props.data - Chart data array
 * @param {React.ReactNode} props.children - Chart component to render
 * @param {boolean} props.loading - Loading state
 * @param {string} props.noDataMessage - Message to show when no data
 * @param {string} props.fileName - Custom filename for CSV download (optional)
 * @param {Object} props.chartCardStyle - Custom styles for chart card (optional)
 * @param {Object} props.chartCardInnerStyle - Custom styles for chart card inner (optional)
 * @param {Object} props.chartTitleStyle - Custom styles for chart title (optional)
 * @param {Object} props.chartSubtitleStyle - Custom styles for chart subtitle (optional)
 */
const ChartWithDownload = ({
    title,
    subtitle,
    data,
    children,
    loading,
    noDataMessage = "No data available for the selected period.",
    fileName,
    chartCardStyle,
    chartCardInnerStyle,
    chartTitleStyle,
    chartSubtitleStyle
}) => {
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0 });
    const chartRef = useRef(null);
    const contextMenuRef = useRef(null);
    const lastRightClickTime = useRef(0);
    const chartId = useRef(`chart-${Math.random().toString(36).substr(2, 9)}`);

    // Handle right-click context menu
    const handleContextMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Prevent rapid successive right-clicks
        const now = Date.now();
        if (now - lastRightClickTime.current < 100) {
            return;
        }
        lastRightClickTime.current = now;
        
        // Ensure this is the correct chart instance - more strict check
        if (!chartRef.current || !chartRef.current.contains(e.target)) {
            return;
        }
        
        // Additional check: ensure the target is actually within this chart's data area
        const targetElement = e.target;
        const chartElement = chartRef.current;
        if (!chartElement.contains(targetElement)) {
            return;
        }
        
        if (hasData) {
            // Get the chart container's position
            const rect = chartRef.current.getBoundingClientRect();
            
            // Calculate position relative to chart container (no scroll offset needed for absolute positioning)
            let x = e.clientX - rect.left;
            let y = e.clientY - rect.top;
            
            // Check if the click is within the chart data area (excluding title and padding)
            const titleHeight = 50; // Approximate title height
            const padding = 20; // Chart padding
            const dataAreaTop = titleHeight + padding;
            const dataAreaHeight = rect.height - dataAreaTop - padding;
            
            // Only show context menu if click is in the data area
            if (y >= dataAreaTop && y <= dataAreaTop + dataAreaHeight) {
                // Position the menu exactly where the user clicked
                let menuX = x;
                let menuY = y;
                
                // Ensure context menu stays within chart bounds
                const menuWidth = 150;
                const menuHeight = 50;
                const chartWidth = rect.width;
                const chartHeight = rect.height;
                
                // Adjust position to keep menu within chart boundaries
                if (menuX + menuWidth > chartWidth - padding) {
                    menuX = chartWidth - menuWidth - padding - 10;
                }
                if (menuY + menuHeight > chartHeight - padding) {
                    menuY = chartHeight - menuHeight - padding - 10;
                }
                if (menuX < padding) menuX = padding;
                if (menuY < dataAreaTop) menuY = dataAreaTop;
                
                // Adjust Y position to account for the chart container's internal structure
                // The context menu is positioned relative to the chart data area, not the full container
                menuY = menuY - dataAreaTop;
                
                // Close any other context menus first
                setContextMenu({ visible: false, x: 0, y: 0 });
                
                // Small delay to ensure state is updated
                setTimeout(() => {
                    setContextMenu({
                        visible: true,
                        x: menuX,
                        y: menuY
                    });
                    
                }, 10);
            }
        }
    };

    // Close context menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            // Only close if the click is outside both the context menu and this specific chart
            if (contextMenuRef.current && !contextMenuRef.current.contains(e.target) && 
                chartRef.current && !chartRef.current.contains(e.target)) {
                setContextMenu({ visible: false, x: 0, y: 0 });
            }
        };

        const handleScroll = () => {
            setContextMenu({ visible: false, x: 0, y: 0 });
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setContextMenu({ visible: false, x: 0, y: 0 });
            }
        };

        if (contextMenu.visible) {
            // Use capture phase to ensure we catch the event before other handlers
            document.addEventListener('click', handleClickOutside, true);
            document.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleScroll, true);
            document.addEventListener('keydown', handleKeyDown, true);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, true);
            document.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleScroll, true);
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [contextMenu.visible, title, chartId]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setContextMenu({ visible: false, x: 0, y: 0 });
        };
    }, []);

    // Global context menu manager - close other context menus when this one opens
    useEffect(() => {
        if (contextMenu.visible) {
            // Close all other context menus by dispatching a custom event
            const closeOtherMenusEvent = new CustomEvent('closeOtherContextMenus', {
                detail: { chartId: chartId.current }
            });
            document.dispatchEvent(closeOtherMenusEvent);
        }
    }, [contextMenu.visible]);

    // Listen for close other context menus event
    useEffect(() => {
        const handleCloseOtherMenus = (e) => {
            if (e.detail.chartId !== chartId.current && contextMenu.visible) {
                setContextMenu({ visible: false, x: 0, y: 0 });
            }
        };

        document.addEventListener('closeOtherContextMenus', handleCloseOtherMenus);
        return () => {
            document.removeEventListener('closeOtherContextMenus', handleCloseOtherMenus);
        };
    }, [contextMenu.visible, title, chartId]);

    // Default styles
    const defaultChartCardStyle = {
        backgroundColor: '#fff',
        borderRadius: 8,
        padding: 20,
        marginBottom: 20,
        outline: 'none',
        position: 'relative',
        overflow: 'hidden',
        ...chartCardStyle
    };

    const defaultChartCardInnerStyle = {
        ...chartCardInnerStyle
    };

    const defaultChartTitleStyle = {
        fontSize: 18,
        fontWeight: 'bold',
        color: '#333',
        marginBottom: subtitle ? 5 : 15,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...chartTitleStyle
    };

    const defaultChartSubtitleStyle = {
        fontSize: 14,
        color: '#666',
        marginBottom: 15,
        ...chartSubtitleStyle
    };

    const contextMenuStyle = {
        position: 'absolute',
        top: contextMenu.y,
        left: contextMenu.x,
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: 4,
        zIndex: 1000,
        minWidth: 150,
        padding: '4px 0',
        display: contextMenu.visible ? 'block' : 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        pointerEvents: 'auto'
    };

    const contextMenuItemStyle = {
        padding: '8px 16px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 14,
        color: '#333',
        border: 'none',
        background: 'none',
        width: '100%',
        textAlign: 'left',
        transition: 'background-color 0.2s'
    };

    // Function to convert data to CSV format
    const convertToCSV = (data) => {
        if (!data || data.length === 0) return '';
        
        // Get all unique keys from the data
        const keys = [...new Set(data.flatMap(item => Object.keys(item)))];
        
        // Create CSV header
        const header = keys.join(',');
        
        // Create CSV rows
        const rows = data.map(item => 
            keys.map(key => {
                const value = item[key] || '';
                // Escape commas and quotes in values
                if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                    return `"${value.replace(/"/g, '""')}"`;
                }
                return value;
            }).join(',')
        );
        
        return [header, ...rows].join('\n');
    };

    // Function to download CSV
    const downloadCSV = () => {
        if (!data || data.length === 0) {
            alert('No data to download');
            return;
        }

        const csvContent = convertToCSV(data);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            
            // Generate filename based on title or use custom fileName
            const defaultFileName = title ? 
                title.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') : 
                'chart_data';
            const finalFileName = fileName || `${defaultFileName}_${new Date().toISOString().split('T')[0]}.csv`;
            
            link.setAttribute('download', finalFileName);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    };

    // Check if data has any non-zero values
    const hasData = data && data.length > 0 && data.some(item => {
        return Object.values(item).some(value => 
            typeof value === 'number' && value > 0
        );
    });

    return (
        <>
            <style>
                {`
                    .chart-container:focus {
                        outline: none !important;
                    }
                    .chart-container:active {
                        outline: none !important;
                    }
                    .chart-container:focus-visible {
                        outline: none !important;
                    }
                        .recharts-wrapper {
  margin: auto;
}
                `}
            </style>
            <div 
                ref={chartRef}
                className="chart-container"
                style={defaultChartCardStyle}
                onContextMenu={handleContextMenu}
                tabIndex={-1}
                data-chart-id={chartId.current}
            >
                <div style={defaultChartCardInnerStyle} className='w-100'>
                    <div style={defaultChartTitleStyle}>
                        <span>{title}</span>
                    </div>
                    {subtitle && (
                        <div style={defaultChartSubtitleStyle}>{subtitle}</div>
                    )}
                    
                    {loading ? (
                        <div style={{ textAlign: 'center', fontSize: 20, padding: 30 }}>
                            Loading...
                        </div>
                    ) : !hasData ? (
                        <div style={{ textAlign: 'center', color: '#888', fontSize: 18, padding: 30 }}>
                            {noDataMessage}
                        </div>
                    ) : (
                        <div style={{ 
                            width: '100%', 
                            height: 420, 
                            outline: 'none', 
                            border: 'none',
                            cursor: 'context-menu',
                            position: 'relative'
                        }}>
                            {children}
                            
                            {/* Context Menu - Inside chart container */}
                            {contextMenu.visible && (
                                <div 
                                    ref={contextMenuRef} 
                                    style={contextMenuStyle}
                                    data-chart-id={chartId.current}
                                    data-chart-title={title}
                                >
                                    <button
                                        style={contextMenuItemStyle}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            downloadCSV();
                                            setContextMenu({ visible: false, x: 0, y: 0 });
                                        }}
                                    >
                                        <FaDownload size={14} />
                                        Export to CSV
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ChartWithDownload;
